import { ReactNode } from "react";
import { cn } from "~/lib/utils";

interface SettingsSectionProps {
  title?: string;
  description?: ReactNode;
  // Applies destructive-token styling for grouping irreversible actions.
  danger?: boolean;
  // Optional control (e.g. an "Add" button) rendered inline with the title.
  headerAction?: ReactNode;
  children: ReactNode;
}

/**
 * Titled card that groups related settings action rows on an entity's Settings
 * page. The `danger` variant gives the irreversible actions (delete / leave) a
 * destructive-accented frame and heading.
 */
export const SettingsSection = ({
  title,
  description,
  danger = false,
  headerAction,
  children,
}: SettingsSectionProps) => (
  <section
    className={cn("rounded-lg border bg-card", danger ? "border-destructive/40" : "border-border")}
  >
    {title && (
      <div
        className={cn(
          "flex items-center justify-between gap-4 border-b px-4 py-3",
          danger ? "border-destructive/40" : "border-border"
        )}
      >
        <div className="min-w-0">
          <h2
            className={cn("text-sm font-semibold", danger ? "text-destructive" : "text-foreground")}
          >
            {title}
          </h2>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="divide-y divide-border">{children}</div>
  </section>
);
